<template>
  <span :style="{ 'background-color': color }" class="px-2 rounded-lg py-1">
    <slot></slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    originalValue?: number;
    newValue?: number;
    minValue?: number;
    maxValue?: number;
    byDiff?: boolean;
  }>(),
  { originalValue: 0, newValue: 0, minValue: 0, maxValue: 0, byDiff: false },
);

const color = computed(() => {
  if (props.byDiff) {
    const diff = props.newValue - props.originalValue;
    return colorScale(
      diff,
      0,
      diff > 0
        ? props.maxValue - props.minValue
        : props.minValue - props.maxValue,
      diff > 0 ? green : red,
      (value) => Math.tanh(3 * value),
    );
  } else {
    return colorScale(props.newValue, props.minValue, props.maxValue);
  }
});

type hsl = { hue: number; saturation: number; light: number };
const green = {
  hue: 122,
  saturation: 39,
  light: 49,
} as const satisfies hsl; // #4CAF50
const red = {
  hue: 4,
  saturation: 90,
  light: 58,
} as const satisfies hsl; // #F44336
const blue = { hue: 207, saturation: 90, light: 54 } as const satisfies hsl; // #2196F3

const colorScale = (
  value: number,
  from = 0,
  to = 100,
  hsl = blue as hsl,
  scaleTransform = (value: number) => value,
): `hsla(${number}, ${number}%, ${number}%, ${number})` => {
  let light = hsl.light;

  const percent = Math.abs(value - from) / Math.abs(from - to);

  const transformedPercent = scaleTransform(percent);

  light = light + (100 - light) * (1 - transformedPercent);
  return `hsla(${hsl.hue}, ${hsl.saturation}%, ${light}%, ${transformedPercent})`;
};
</script>
